import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Moment from 'react-moment';


const useAudioPlayer = (audioRef) => {
  const [duration, setDuration] = useState();
  const [curTime, setCurTime] = useState();
  const [playing, setPlaying] = useState(false);
  const [clickedTime, setClickedTime] = useState();

  const curPercentage = (curTime / duration) * 100;

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    // state setters wrappers
    const setAudioData = () => {
      setDuration(audio.duration);
      setCurTime(audio.currentTime);
    }

    const setAudioTime = () => setCurTime(audio.currentTime);

    // DOM listeners: update React state on DOM events
    audio.addEventListener("loadeddata", setAudioData);

    audio.addEventListener("timeupdate", setAudioTime);

    // React state listeners: update DOM on React state changes
    playing ? audio.play() : audio.pause();

    if (clickedTime && clickedTime !== curTime) {
      audio.currentTime = clickedTime;
      setClickedTime(null);
    } 

    // effect cleanup
    return () => {
      audio.removeEventListener("loadeddata", setAudioData);
      audio.removeEventListener("timeupdate", setAudioTime);
    }
  }, [audioRef, clickedTime, curTime, playing]);

  return {
    curPercentage,
    curTime,
    duration,
    playing,
    setPlaying,
    setClickedTime
  }
}

const MinutesSeconds = ({totalSeconds}) => {
  const pad = (num, size) => {
    num = num.toString();
    while (num.length < size) num = "0" + num;
    return num;
  }

  const format = (totalSeconds) => {
    if (!totalSeconds) {
      return "00:00"
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds - minutes * 60);

    return `${pad(minutes, 2)}:${pad(seconds, 2)}`
  }

  return <>{format(totalSeconds)}</>
}

const StatusBadge = ({ status }) => {
  if (status === "done") {
    return <span class="badge badge-pill done">Done</span>
  }

  if (status === "published") {
    return <span class="badge badge-pill published">Published</span>
  }

  return <span class="badge badge-pill in-progress">In progress</span>
}

const PlayerCardContent = ({ title, podcastUrl, fileUrl, createdAt, status, showLinks = true, showActions = true, extraInfo = "" }) => {
  const audioRef = useRef();
  const progressBarRef = useRef();
  const audioPlayer = useAudioPlayer(audioRef);
  const { curPercentage, setClickedTime, duration, playing, setPlaying } = audioPlayer;

  const calcClickedTime = (e) => {
    const clickPositionInPage = e.pageX;
    const bar = progressBarRef.current;
    const barStart = bar.getBoundingClientRect().left + window.scrollX;
    const barWidth = bar.offsetWidth;
    const clickPositionInBar = clickPositionInPage - barStart;
    const timePerPixel = duration / barWidth;
    return timePerPixel * clickPositionInBar;
  }

  const handleTimeDrag = (e) => {
    setClickedTime(calcClickedTime(e));

    const updateTimeOnMove = eMove => {
      setClickedTime(calcClickedTime(eMove));
    };

    document.addEventListener("mousemove", updateTimeOnMove);

    document.addEventListener("mouseup", () => {
      document.removeEventListener("mousemove", updateTimeOnMove);
    });
  }

  return (
    <>
      <div class="row no-gutters">
        <div class="col-12 col-md-7">
          <h3 class="mb-0">
            { showLinks ? (
              <Link to={podcastUrl}>{title}</Link>
            ) : (
              <>{title}</>
            )}
          </h3>
        </div>
        <div class="col-12 col-md-5">
          <ul class="list-unstyled player-details">
            { fileUrl ? (
              <li>
                <span className="current-position">
                  <MinutesSeconds totalSeconds={audioPlayer.curTime} />
                </span>
                &nbsp;
                <span className="length">
                  <MinutesSeconds totalSeconds={audioPlayer.duration} />
                </span>
              </li>
            ) : (
              <li>&nbsp;</li>
            )}
            { extraInfo && (
              <li>
                {extraInfo}
              </li>
            )}
            <li>
              <span class="updated">
                <Moment format="DD MMM">{createdAt}</Moment>
              </span>
            </li>
            <li>
              <StatusBadge status={status} />
            </li>
          </ul>
        </div>
      </div>
      { fileUrl && (
        <audio ref={audioRef}>
          <source src={fileUrl} />
        </audio>
      )}
      <div class="player-controls">
        <div class={`play ${playing && "playing"}`} onClick={() => {
          if (fileUrl) setPlaying(!playing)
        }}>
          <i class="fas fa-pause-circle"></i>
          <i class="fas fa-play-circle"></i>
        </div>
        <div class="progress" ref={progressBarRef} onMouseDown={e => handleTimeDrag(e)}>
          <div
            class="progress-bar"
            role="progressbar"
            style={{width: `${curPercentage}%`}}
            aria-valuenow={curPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
      </div>
      { showActions && showLinks && (
        <div class="player-actions">
          <ul class="list-unstyled mb-0">
            <li class="mb-0">
              <Link to={podcastUrl}>
                Comment
              </Link>
            </li>
            <li class="mb-0">
              <Link to={podcastUrl}>
                More Info
              </Link>
            </li>
          </ul>
        </div>
      )}
    </>
  )
}

const PlayerCardSingle = ({podcast, showLinks = true, extraInfo = ""}) => {
  const podcastUrl = `/podcasts/details/${podcast.id}`;
  const fileUrl = (
    Array.isArray(podcast.narrations) &&
    podcast.narrations.length > 0 &&
    podcast.narrations[0].fileUrl
  )

  const status = (
    Array.isArray(podcast.narrations) &&
    podcast.narrations.length > 0 &&
    podcast.narrations[0].status
  )

  return (
    <div class={`card player ${fileUrl && "playable"}`}>
      <PlayerCardContent
        title={podcast.title}
        podcastUrl={podcastUrl}
        fileUrl={fileUrl}
        status={status}
        createdAt={podcast?.createdAt?.toString()}
        showLinks={showLinks}
        extraInfo={extraInfo}
        />
    </div>
  )
}

export const PlayerCardMultiple = ({podcast, showLinks = true, extraInfo = ""}) => {
  const episodeCount = Array.isArray(podcast.narrations) ? podcast.narrations.length : 0;
  const podcastUrl = `/podcasts/details/${podcast.id}`;

  return (
    <div class="card multiple">
      <h2 class="p-3 mb-0">
        { showLinks ? (
          <Link to={podcastUrl}>
            {podcast.title}
          </Link>
        ) : (
          <>{podcast.title}</>
        )}
      </h2>
      <div className="episode-count mb-3">
        <span>{episodeCount} episodes</span>
      </div>
      { podcast.narrations.map((narration, i) => (
        <div className={`player ${narration.fileUrl && "playable"}`}>
          <PlayerCardContent
            title={narration.title}
            podcastUrl={podcastUrl}
            fileUrl={narration.fileUrl}
            status={narration.status}
            createdAt={podcast?.createdAt?.toString()}
            showActions={podcast.narrations.length === i + 1}
            showLinks={showLinks}
            extraInfo={extraInfo}
            />
        </div>
      ))}
    </div>
  )
}

export const PlayerCard = ({podcast, showLinks = true, extraInfo = ""}) => {
  if (Array.isArray(podcast.narrations) && podcast.narrations.length > 1) {
    return <PlayerCardMultiple podcast={podcast} showLinks={showLinks} extraInfo={extraInfo} />
  }

  return <PlayerCardSingle podcast={podcast} showLinks={showLinks} extraInfo={extraInfo} />
}
